import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Input, InputNumber, Popconfirm, Select, Space, Switch, Table, Tooltip } from 'antd';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import type { RcdLabelInformation, RcdMeasurement, UsedRcd } from '../types/rcdMeasurement';
import { validateRcdDecimal, zeroingCompliance } from '../utils/rcdMeasurements';
import { previewRcdMapping } from '../services/controlTaskService';
import type { EarthingMeasurement, EarthingMeasurementPointGroup, RcdMappingPreview } from '../services/controlTaskService';
import type { NetworkType } from '../../../utils/networkType';
import { complianceNoteOptions as noteOptions } from '../utils/complianceNotes';
import ComplianceNoteTooltip from './ComplianceNoteTooltip';
import './RcdMeasurementsTable.scss';

const sourceKey = (row: RcdMeasurement): string | undefined => row.sourceEarthingMeasurementId != null
  ? `id:${row.sourceEarthingMeasurementId}` : row.sourceEarthingClientKey ? `new:${row.sourceEarthingClientKey}` : undefined;
const earthingKey = (row: EarthingMeasurement): string | undefined => row.id != null
  ? `id:${row.id}` : row.clientKey ? `new:${row.clientKey}` : undefined;
const rcdSourceGroups: ReadonlySet<EarthingMeasurementPointGroup> = new Set<EarthingMeasurementPointGroup>([
  'MAIN_PANEL',
  'SUB_PANEL',
  'FIRE_PANEL',
]);
const rcdLabelTypeOptions = [
  { value: 'RCD', label: 'RCD' },
  { value: 'TOROID', label: 'TOROİD' },
];


type MeasurementsForm = { measurements: RcdMeasurement[] };
type TableRow = { key: number; name: number };
type FieldPath =
  | [Exclude<keyof RcdMeasurement, 'rcdLabelInformation' | 'usedRcd' | 'id' | 'reportNodeId' | 'rowOrder'>]
  | ['rcdLabelInformation', keyof RcdLabelInformation]
  | ['usedRcd', keyof UsedRcd];

export type RcdMeasurementsTableHandle = { validate: () => Promise<void> };
type Props = {
  value: RcdMeasurement[];
  sources?: EarthingMeasurement[];
  networkType?: NetworkType;
  onChange: (value: RcdMeasurement[]) => void;
  disabled?: boolean;
  loading?: boolean;
};

const decimalRule = {
  validator: (_rule: unknown, value: unknown) => {
    const error = validateRcdDecimal(value);
    return error ? Promise.reject(new Error(error)) : Promise.resolve();
  },
};

const RcdMeasurementsTable = forwardRef<RcdMeasurementsTableHandle, Props>(
  ({ value, onChange, sources = [], networkType, disabled = false, loading = false }, ref) => {
    const [form] = Form.useForm<MeasurementsForm>();
    const lastSyncedValue = useRef(JSON.stringify(value));
    const lastReceivedValue = useRef(JSON.stringify(value));
    const resetVersion = useRef(0);
    const serializedValue = JSON.stringify(value);
    if (serializedValue !== lastReceivedValue.current && serializedValue !== lastSyncedValue.current) {
      resetVersion.current += 1;
    }
    lastReceivedValue.current = serializedValue;
    const readOnly = disabled || loading;

    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const [previewErrors, setPreviewErrors] = useState<Record<string, string>>({});
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewRetry, setPreviewRetry] = useState(0);
    const previewBusy = useRef(false);
    const completedPreviewInput = useRef<string>();
    const previewErrorRef = useRef<Record<string, string>>({});
    const sourceOptions = sources.flatMap((source) => {
      if (!source.measurementPointGroup || !rcdSourceGroups.has(source.measurementPointGroup)) return [];
      const key = earthingKey(source);
      return key ? [{ value: key, label: `${source.measurementPointDescription || 'Adsız ölçüm'} (${source.id ?? source.clientKey})` }] : [];
    });
    const previewInput = JSON.stringify({
      resetVersion: resetVersion.current,
      networkType,
      links: value.map(row => ({ id: row.id, key: sourceKey(row),
        zeroingDetected: row.zeroingDetected === true,
        phaseNeutralLoopImpedanceOhm: row.phaseNeutralLoopImpedanceOhm,
        neutralToEarthVoltageV: row.neutralToEarthVoltageV })),
      sources: sources.map(row => ({ id: row.id, clientKey: row.clientKey,
        ratedCurrentA: row.ratedCurrentA, protectiveConductorCrossSectionMm2: row.protectiveConductorCrossSectionMm2,
        tripCurveTypeOrModel: row.tripCurveTypeOrModel,
        measurementPointDescription: row.measurementPointDescription,
        measurementPointGroup: row.measurementPointGroup,
        measurementEnvironmentCondition: row.measurementEnvironmentCondition,
        rcdRatingMa: row.rcdRatingMa,
        phaseEarthLoopImpedanceOhm: row.phaseEarthLoopImpedanceOhm,
        rcdTripCurrentMa: row.rcdTripCurrentMa,
        rcdTripCurrentDidNotTrip: row.rcdTripCurrentDidNotTrip,
        toroidRatingA: row.toroidRatingA,
        rcdTripTimeMs: row.rcdTripTimeMs,
        rcdTripTimeDidNotTrip: row.rcdTripTimeDidNotTrip })),
    });

    const latestPreviewInput = useRef(previewInput);
    latestPreviewInput.current = previewInput;

    useImperativeHandle(ref, () => ({
      validate: async () => {
        await form.validateFields();
        if (previewBusy.current || completedPreviewInput.current !== latestPreviewInput.current) throw new Error('RCD önizlemesinin tamamlanmasını bekleyiniz.');
        if (Object.keys(previewErrorRef.current).length) throw new Error('RCD kaynak hatalarını düzeltiniz.');
      },
    }), [form]);

    useEffect(() => {
      const serialized = JSON.stringify(value);
      if (serialized !== lastSyncedValue.current) {
        // Replace the list so reset/reload cannot retain fields from the previous report.
        form.setFieldValue('measurements', value);
        lastSyncedValue.current = serialized;
      }
    }, [form, value]);

    useEffect(() => {
      const input: { networkType?: NetworkType; links: { key?: string;
        zeroingDetected: boolean;
        phaseNeutralLoopImpedanceOhm?: RcdMeasurement['phaseNeutralLoopImpedanceOhm'];
        neutralToEarthVoltageV?: RcdMeasurement['neutralToEarthVoltageV'] }[];
        sources: EarthingMeasurement[] } = JSON.parse(previewInput);
      const keys = input.links.map((_row, index) => String(index));
      const controller = new AbortController();
      let active = true;
      previewBusy.current = keys.length > 0;
      setPreviewLoading(keys.length > 0);
      previewErrorRef.current = {};
      setPreviewErrors({});
      // Immediately remove stale results while the authoritative preview is pending.
      const currentRows: RcdMeasurement[] = form.getFieldValue('measurements') ?? [];
      const pendingRows = currentRows.map(row => {
        const source = input.sources.find(item => earthingKey(item) === sourceKey(row) && sourceKey(row) != null);
        const linked = !!sourceKey(row);
        return { ...row,
          zeroingDetected: row.zeroingDetected === true,
          phaseEarthLoopImpedanceOhm: source?.phaseEarthLoopImpedanceOhm === '' ? null : source?.phaseEarthLoopImpedanceOhm ?? null,
          calculatedThreePhaseShortCircuitCurrentKa: null,
          usedRcd: linked ? { ...row.usedRcd, rcdType: null, testTripTimeMs: null,
            testTripTimeDidNotTrip: false } : row.usedRcd,
          ...(row.zeroingDetected === true ? zeroingCompliance : {
            generatedComplianceResult: null,
            complianceCalculationStatus: undefined, complianceCalculationMessage: undefined,
          }),
        };
      });
      if (JSON.stringify(pendingRows) !== JSON.stringify(currentRows)) {
        form.setFieldValue('measurements', pendingRows);
        lastSyncedValue.current = JSON.stringify(pendingRows);
        onChangeRef.current(pendingRows);
      }
      const timer = window.setTimeout(async () => {
        const results = await Promise.all(keys.map(async key => {
          const link = input.links[Number(key)];
          const source = input.sources.find(row => link.key != null && earthingKey(row) === link.key);
          try {
            if (link.key && !source) throw new Error('Kaynak silinmiş veya bulunamadı. Bağlantıyı kaldırın ya da başka kaynak seçin.');
            const result = await previewRcdMapping(source ?? {}, controller.signal, {
              networkType: input.networkType,
              phaseNeutralLoopImpedanceOhm: link.phaseNeutralLoopImpedanceOhm,
              neutralToEarthVoltageV: link.neutralToEarthVoltageV,
              zeroingDetected: link.zeroingDetected,
            });
            return { key, result, error: undefined };
          } catch (error) {
            return { key, result: undefined, error: error instanceof Error ? error.message : 'RCD önizlemesi alınamadı.' };
          }
        }));
        if (!active || latestPreviewInput.current !== previewInput) return;
        const errors: Record<string, string> = {};
        const calculated = new Map<string, RcdMappingPreview>();
        results.forEach(({ key, result, error }) => {
          if (result) calculated.set(key, result);
          if (error) errors[key] = error;
        });
        previewErrorRef.current = errors;
        setPreviewErrors(errors);
        previewBusy.current = false;
        completedPreviewInput.current = previewInput;
        setPreviewLoading(false);
        const current: RcdMeasurement[] = form.getFieldValue('measurements') ?? [];
        const updated = current.map((row, index) => {
          const linked = !!sourceKey(row);
          const result = calculated.get(String(index));
          return result ? { ...row, finalCircuitPanelName: linked ? result.finalCircuitPanelName : row.finalCircuitPanelName,
            phaseEarthLoopImpedanceOhm: result.phaseEarthLoopImpedanceOhm ?? null,
            calculatedThreePhaseShortCircuitCurrentKa: result.calculatedThreePhaseShortCircuitCurrentKa ?? null,
            usedRcd: linked ? { rcdType: result.rcdType,
              ratedResidualCurrentMa: row.usedRcd?.ratedResidualCurrentMa ?? null,
              testTripTimeMs: result.testTripTimeMs,
              testTripTimeDidNotTrip: result.testTripTimeDidNotTrip } : row.usedRcd,
            ...(row.complianceResultOverridden === false ? { complianceResult: result.generatedComplianceResult ?? null } : {}),
            generatedComplianceResult: result.generatedComplianceResult ?? null,
            complianceCalculationStatus: result.complianceCalculationStatus,
            complianceCalculationMessage: result.complianceCalculationMessage,
            ...(row.zeroingDetected === true ? zeroingCompliance : {}),
          } : row;
        });
        if (JSON.stringify(updated) !== JSON.stringify(current)) {
          form.setFieldValue('measurements', updated);
          lastSyncedValue.current = JSON.stringify(updated);
          onChangeRef.current(updated);
        }
      }, keys.length ? 250 : 0);
      return () => { active = false; controller.abort(); window.clearTimeout(timer); previewBusy.current = false; };
    }, [previewInput, previewRetry, form]);

    const emitValues = () => {
      const values: MeasurementsForm = form.getFieldsValue(true);
      const rows = values.measurements ?? [];
      lastSyncedValue.current = JSON.stringify(rows);
      onChange(rows);
    };

    const fieldColumn = (
      title: string,
      path: FieldPath,
      width: number,
      numeric = false,
      maxLength?: number,
    ): ColumnType<TableRow> => ({
      title,
      key: path.join('.'),
      width,
      render: (_value: unknown, row: TableRow) => {
        const group = path[0] === 'rcdLabelInformation' ? 'Kullanılan RCD Etiket Bilgileri – '
          : path[0] === 'usedRcd' ? 'Kullanılan RCD – ' : '';
        const label = `${group}${title} satır ${row.name + 1}`;
        const linked = !!sourceKey(form.getFieldValue(['measurements', row.name]) ?? {});
        const sourceDerived = path[0] === 'phaseEarthLoopImpedanceOhm'
          || path[0] === 'calculatedThreePhaseShortCircuitCurrentKa';
        const sourceMappedUsedRcd = path[0] === 'usedRcd' && path[1] !== 'ratedResidualCurrentMa';
        const automatic = sourceDerived || (linked && (sourceMappedUsedRcd || path[0] === 'finalCircuitPanelName'));
        return (
          <Form.Item
            name={[row.name, ...path]}
            getValueProps={automatic && path[0] === 'usedRcd' && path[1] === 'rcdType'
              ? (current) => ({ value: current === 'TOROID' ? 'Toroid' : current }) : undefined}
            rules={sourceDerived ? [] : numeric ? [decimalRule] : maxLength ? [{ max: maxLength, message: `En fazla ${maxLength} karakter girilebilir.` }] : []}
          >
            {numeric ? (
              <InputNumber
                stringMode
                controls={false}
                disabled={readOnly || automatic}
                aria-label={label}
                className='rcd-measurements-table__input'
              />
            ) : (
              <Input disabled={readOnly || automatic} aria-label={label} maxLength={maxLength} />
            )}
          </Form.Item>
        );
      },
    });

    const testTripTimeColumn: ColumnType<TableRow> = {
      title: 'Test Açma Zamanı TΔ (ms)',
      key: 'usedRcd.testTripTimeMs',
      width: 180,
      render: (_value, row) => {
        const current: RcdMeasurement = form.getFieldValue(['measurements', row.name]) ?? {};
        const label = `Kullanılan RCD – Test Açma Zamanı TΔ (ms) satır ${row.name + 1}`;
        if (current.usedRcd?.testTripTimeDidNotTrip === true) {
          return <Form.Item><Input disabled aria-label={label} value='AÇMADI' /></Form.Item>;
        }
        return <Form.Item name={[row.name, 'usedRcd', 'testTripTimeMs']} rules={[decimalRule]}>
          <InputNumber stringMode controls={false} disabled={readOnly || !!sourceKey(current)}
            aria-label={label} className='rcd-measurements-table__input' />
        </Form.Item>;
      },
    };

    const rcdLabelTypeColumn: ColumnType<TableRow> = {
      title: 'RCD Tipi',
      key: 'rcdLabelInformation.rcdType',
      width: 150,
      render: (_value, row) => (
        <Form.Item name={[row.name, 'rcdLabelInformation', 'rcdType']}>
          <Select
            aria-label={`Kullanılan RCD Etiket Bilgileri – RCD Tipi satır ${row.name + 1}`}
            className='rcd-measurements-table__input'
            disabled={readOnly}
            options={rcdLabelTypeOptions}
          />
        </Form.Item>
      ),
    };

    return (
      <section className='rcd-measurements-table' aria-label='RCD ölçümleri' aria-busy={loading || previewLoading}>
        {previewLoading ? <Alert type='info' message='RCD alanları hesaplanıyor…' /> : null}
        {Object.keys(previewErrors).length ? <Alert type='error' message='RCD kaynak hatalarını düzeltiniz.' action={<Button disabled={readOnly} onClick={() => setPreviewRetry(count => count + 1)}>Yeniden dene</Button>} /> : null}
        <Form<MeasurementsForm>
          form={form}
          component={false}
          initialValues={{ measurements: value }}
          onValuesChange={emitValues}
        >
          <Form.List name='measurements'>
            {(fields, { add, remove }) => {
              const columns: ColumnsType<TableRow> = [
                {
                  title: 'ARTIK AKIM ANAHTARLARI (RCD) SELEKTİVİTE KONTROLÜ',
                  children: [
                    { title: 'No', key: 'rowOrder', width: 70, align: 'center', render: (_v, row) => row.name + 1 },
                    { title: 'Topraklama Kaynağı', key: 'source', width: 280,
                      render: (_value, row) => {
                        const current: RcdMeasurement = form.getFieldValue(['measurements', row.name]) ?? {};
                        const key = sourceKey(current);
                        return <Form.Item validateStatus={previewErrors[String(row.name)] ? 'error' : undefined}
                          help={previewErrors[String(row.name)]}>
                          <Select showSearch allowClear optionFilterProp='label' disabled={readOnly}
                            aria-label={`Topraklama kaynağı satır ${row.name + 1}`}
                            placeholder='Topraklama satırı seçiniz' value={key} options={sourceOptions}
                            onChange={(selected?: string) => {
                              const source = sources.find(item => earthingKey(item) === selected);
                              form.setFieldValue(['measurements', row.name], { ...current,
                                sourceEarthingMeasurementId: source?.id ?? null,
                                sourceEarthingClientKey: source?.id == null ? source?.clientKey ?? null : null,
                                finalCircuitPanelName: null,
                                usedRcd: { rcdType: null,
                                  ratedResidualCurrentMa: current.usedRcd?.ratedResidualCurrentMa ?? null,
                                  testTripTimeMs: null, testTripTimeDidNotTrip: false },
                                phaseEarthLoopImpedanceOhm: source?.phaseEarthLoopImpedanceOhm ?? null,
                                calculatedThreePhaseShortCircuitCurrentKa: null });
                              emitValues();
                            }} />
                        </Form.Item>;
                      } },
                    fieldColumn('Son Tüketim Noktasını Besleyen Panodan Önceki Panonun Adı', ['upstreamPanelName'], 280),
                    {
                      title: 'Kullanılan RCD Etiket Bilgileri',
                      children: [
                        rcdLabelTypeColumn,
                        fieldColumn('Dayanma Akımı In (A)', ['rcdLabelInformation', 'ratedCurrentA'], 160, true),
                        fieldColumn('Açma Akımı IΔn (mA)', ['rcdLabelInformation', 'ratedResidualCurrentMa'], 160, true),
                        fieldColumn('Açma Zamanı Gecikme (ms)', ['rcdLabelInformation', 'tripTimeDelayMs'], 180, true),
                      ],
                    },
                    fieldColumn('Son Tüketim Noktasını Besleyen Pano Adı', ['finalCircuitPanelName'], 280),
                    {
                      title: 'Kullanılan RCD',
                      children: [
                        fieldColumn('RCD Tipi', ['usedRcd', 'rcdType'], 150, false, 255),
                        fieldColumn('Açma Akımı IΔn (mA)', ['usedRcd', 'ratedResidualCurrentMa'], 160, true),
                        testTripTimeColumn,
                      ],
                    },
                    { title: 'Sıfırlama', key: 'zeroingDetected', width: 130, align: 'center',
                      render: (_value, row) => {
                        const current: RcdMeasurement = form.getFieldValue(['measurements', row.name]) ?? {};
                        return <Form.Item>
                          <Switch aria-label={`Sıfırlama satır ${row.name + 1}`}
                            checked={current.zeroingDetected === true} disabled={readOnly}
                            onChange={(checked) => {
                              completedPreviewInput.current = undefined;
                              form.setFieldValue(['measurements', row.name], { ...current,
                                zeroingDetected: checked,
                                ...(checked ? zeroingCompliance : {
                                  complianceResultOverridden: false,
                                  complianceResult: null, generatedComplianceResult: null,
                                  complianceCalculationStatus: undefined, complianceCalculationMessage: undefined,
                                }) });
                              emitValues();
                            }} />
                        </Form.Item>;
                      } },
                    { title: 'Sonuç (Uygunluk Notu)', key: 'complianceResult', width: 240,
                      render: (_value, row) => {
                        const current: RcdMeasurement = form.getFieldValue(['measurements', row.name]) ?? {};
                        const zeroing = current.zeroingDetected === true;
                        const generatedNote = zeroing ? zeroingCompliance.generatedComplianceResult : current.generatedComplianceResult;
                        const legacy = current.complianceResult && !noteOptions.some(note => note.value === current.complianceResult);
                        const manual = !zeroing && (current.complianceResultOverridden === true
                          || (current.complianceResultOverridden === undefined && !!current.complianceResult));
                        const unavailable = !zeroing && (previewLoading || completedPreviewInput.current !== previewInput
                          || !!previewErrors[String(row.name)]);
                        const explanation = zeroing ? zeroingCompliance.complianceCalculationMessage : current.complianceCalculationMessage
                          || (current.complianceCalculationStatus === 'INSUFFICIENT_DATA' ? 'Otomatik değerlendirme için gerekli bilgiler eksik.'
                            : current.complianceCalculationStatus === 'NO_MATCH' ? 'Girilen değerler hiçbir otomatik uygunluk koşulunu sağlamıyor.' : undefined);
                        return <Space direction='vertical' className='w-full'>
                          <ComplianceNoteTooltip note={unavailable ? null : generatedNote} fullWidth>
                            <Input readOnly aria-label={`Otomatik uygunluk notu satır ${row.name + 1}`}
                              value={unavailable ? '' : generatedNote ?? ''}
                              placeholder={previewLoading ? 'Hesaplanıyor…' : 'Otomatik sonuç yok'} />
                          </ComplianceNoteTooltip>
                          {!unavailable && explanation ?
                            <span role='status'>{explanation}</span> : null}
                          <span>{manual ? 'Manuel seçim etkin' : 'Otomatik seçim etkin'}</span>
                          <ComplianceNoteTooltip note={manual ? current.complianceResult : null} fullWidth focusable={readOnly || zeroing}>
                            <Select aria-label={`Sonuç (Uygunluk Notu) satır ${row.name + 1}`} disabled={readOnly || zeroing}
                              className='w-full' placeholder='Manuel not seçiniz'
                              value={manual ? current.complianceResult : undefined}
                              options={legacy ? [{ value: current.complianceResult!, label: `${current.complianceResult} (eski kayıt)`, disabled: true }, ...noteOptions] : noteOptions}
                              onChange={(selected: string) => {
                                form.setFieldValue(['measurements', row.name, 'complianceResult'], selected);
                                form.setFieldValue(['measurements', row.name, 'complianceResultOverridden'], true);
                                emitValues();
                              }} />
                          </ComplianceNoteTooltip>
                          <Button size='small' disabled={readOnly || zeroing || !manual || unavailable}
                            onClick={() => {
                              form.setFieldValue(['measurements', row.name, 'complianceResultOverridden'], false);
                              form.setFieldValue(['measurements', row.name, 'complianceResult'], current.generatedComplianceResult ?? null);
                              emitValues();
                            }}>Otomatik seçime dön</Button>
                        </Space>;
                      } },
                  ],
                },
                {
                  title: 'AŞIRI AKIM CİHAZI / İLETKEN UYGUNLUĞU – GERİLİM KONTROLÜ – AŞIRI GERİLİM CİHAZLARI (DKD) KONTROLÜ',
                  children: [
                    fieldColumn('Panodan Ölçülen Faz-Toprak Çevrim Empedansı (Zx) (Ω)', ['phaseEarthLoopImpedanceOhm'], 220, true),
                    fieldColumn('Panodan Ölçülen Faz-Nötr Çevrim Empedansı (ZLN) (Ω)', ['phaseNeutralLoopImpedanceOhm'], 220, true),
                    fieldColumn('Hesaplanan 3 Fazlı Kısa Devre Akımı (kA)', ['calculatedThreePhaseShortCircuitCurrentKa'], 200, true),
                    {
                      title: 'Aşırı Gerilim Koruma',
                      children: [
                        fieldColumn('DKD Tipi', ['circuitBreakerType'], 150, false, 255),
                        fieldColumn('DKD Dayanma Akımı (kA)', ['circuitBreakerBreakingCapacityKa'], 180, true),
                      ],
                    },
                    {
                      title: 'Gerilimler',
                      children: [
                        fieldColumn('F-F (V)', ['phaseToPhaseVoltageV'], 150, true),
                        fieldColumn('L-N (V)', ['phaseToNeutralVoltageV'], 150, true),
                        fieldColumn('N-PE (V)', ['neutralToEarthVoltageV'], 150, true),
                      ],
                    },
                  ],
                },
                {
                  title: 'İşlemler', key: 'actions', width: 100, fixed: 'right', align: 'center',
                  render: (_value, row) => (
                    <Popconfirm
                      title='Bu RCD ölçüm satırını silmek istediğinize emin misiniz?'
                      okText='Sil'
                      cancelText='Vazgeç'
                      disabled={readOnly}
                      onConfirm={() => remove(row.name)}
                    >
                      <Tooltip title='RCD ölçüm satırını sil'>
                        <Button
                          type='text' danger icon={<DeleteOutlined />} disabled={readOnly}
                          aria-label={`RCD ölçüm satırı ${row.name + 1} sil`}
                        />
                      </Tooltip>
                    </Popconfirm>
                  ),
                },
              ];
              const totalWidth = (items: ColumnsType<TableRow>): number => items.reduce(
                (sum, column) => sum + ('children' in column ? totalWidth(column.children) : Number(column.width)), 0,
              );
              return (
                <Space direction='vertical' size={12} className='rcd-measurements-table__content'>
                  <Table<TableRow>
                    bordered size='small' pagination={false} tableLayout='fixed'
                    columns={columns} dataSource={fields.map(({ key, name }) => ({ key, name }))}
                    scroll={{ x: totalWidth(columns) }} loading={loading}
                    locale={{ emptyText: 'Henüz RCD ölçüm satırı eklenmedi.' }}
                  />
                  <Button type='dashed' icon={<PlusOutlined />} disabled={readOnly} onClick={() => add({ zeroingDetected: false })}>
                    RCD Satırı Ekle
                  </Button>
                </Space>
              );
            }}
          </Form.List>
        </Form>
      </section>
    );
  },
);
RcdMeasurementsTable.displayName = 'RcdMeasurementsTable';
export default RcdMeasurementsTable;
